/**
 * Semantics layer for the Calm Health design system — function-named tokens that
 * each carry a {light, dark} value pair resolving onto the primitives layer.
 * Application code should consume semantics (via the MUI palette built in a later
 * step of the design-system token migration), not raw primitives. Values are a
 * transcription of the Figma Semantics column (node 5163:14219); re-pull them
 * with the `design-tokens-figma-sync` skill.
 *
 * Additive only: the legacy DS_* layer and the primitives layer are untouched.
 */
import { lit, semantic } from '../dtcg';
import { FIGMA_DESCRIPTIONS } from '../figma-descriptions.generated';
import { PRIMITIVES } from '../primitives';
/**
 * Project a primitives sub-object (whose digit-leading keys are `_`-prefixed,
 * e.g. `_800`) back to Figma path keys (`color/neutral/800`). Alphabetic keys
 * (`sm`, `md`, `full`) pass through unchanged. The literal key types are
 * preserved so the spread into `PRIMITIVE_REFS` keeps every path as a known key —
 * that is what makes a mistyped primitive reference a tsc error.
 */
function flattenRamp(prefix, ramp) {
    return Object.fromEntries(Object.entries(ramp).map(([k, v]) => [
        `${prefix}/${k.replace(/^_/, '')}`,
        v,
    ]));
}
/**
 * Flatten PRIMITIVES into Figma-style path strings -> concrete values. Semantic
 * mode values that reference a primitive use these keys; the key union
 * (`PrimitiveRef`) is what makes a mistyped path a tsc error.
 */
export const PRIMITIVE_REFS = {
    'color/white': PRIMITIVES.color.white,
    'color/black': PRIMITIVES.color.black,
    ...flattenRamp('color/neutral', PRIMITIVES.color.neutral),
    ...flattenRamp('color/blue', PRIMITIVES.color.blue),
    ...flattenRamp('color/purple', PRIMITIVES.color.purple),
    ...flattenRamp('color/red', PRIMITIVES.color.red),
    ...flattenRamp('color/orange', PRIMITIVES.color.orange),
    ...flattenRamp('color/green', PRIMITIVES.color.green),
    ...flattenRamp('color/cyan', PRIMITIVES.color.cyan),
    ...flattenRamp('overlay/white', PRIMITIVES.overlay.white),
    ...flattenRamp('overlay/black', PRIMITIVES.overlay.black),
    ...flattenRamp('shadow', PRIMITIVES.shadow),
    ...flattenRamp('blur', PRIMITIVES.blur),
    ...flattenRamp('spacing', PRIMITIVES.spacing),
    // Figma names the radius collection "corner radius"; the repo's primitive
    // group is `radius`. The semantics follow Figma, so the ref keys do too.
    ...flattenRamp('corner radius', PRIMITIVES.radius),
};
/**
 * The flat source of truth: every function-named semantic token keyed by its
 * Figma slash-path, carrying a {light, dark} pair. Each mode value is either a
 * typed primitive reference (a mistyped path fails tsc) or a raw `lit()` hex for
 * feature palettes that share no vocabulary with the primitive ramps. Values
 * transcribed from the authoritative Figma snapshot.
 */
export const SEMANTICS_SRC = {
    // --- Surface (70) ---
    'surface/default': semantic('color/white', 'color/neutral/800'),
    'surface/nav-action': semantic('overlay/white/80', 'overlay/black/40'),
    'surface/card': semantic('color/white', 'overlay/black/20'),
    'surface/icon-container': semantic('color/white', 'overlay/black/20'),
    // State overlays for hover/press on flat, fixed-color surfaces (action menus,
    // popovers, dropdown rows): mode-symmetric — darken in light, lift in dark.
    // Controls on gradient backgrounds use interactive/subtle-hover/-pressed instead.
    'surface/hover': semantic('overlay/black/5', 'overlay/white/5'),
    'surface/pressed': semantic('overlay/black/10', 'overlay/white/10'),
    'surface/input': semantic('color/white', 'overlay/black/20'),
    'surface/input-disabled': semantic('overlay/black/10', 'overlay/white/10'),
    'surface/callout': semantic('overlay/black/80', 'color/white'),
    'surface/scrim': semantic('overlay/black/40', 'overlay/black/60'),
    'surface/skeleton': semantic('overlay/black/10', 'overlay/white/20'),
    'surface/sheet/default': semantic('overlay/white/60', 'overlay/black/40'),
    'surface/sheet/solid': semantic('color/neutral/50', 'color/neutral/800'),
    'surface/dialog/default': semantic('color/white', 'color/neutral/800'),
    'surface/dialog/bold': semantic('color/neutral/800', 'color/white'),
    'surface/overlay/light': semantic('overlay/white/20', 'overlay/black/20'),
    'surface/overlay/heavy': semantic('overlay/white/60', 'overlay/black/60'),
    'surface/overlay/intense': semantic('overlay/white/80', 'overlay/black/80'),
    'surface/overlay/transparent': semantic('overlay/white/0', 'overlay/black/0'),
    'surface/card-solid': semantic('color/white', 'color/neutral/800'),
    'surface/tile/default': semantic('color/white', 'overlay/black/20'),
    'surface/tile/disabled': semantic('overlay/black/10', 'overlay/black/10'),
    'surface/accent/purple': semantic('color/purple/100', 'color/purple/700'),
    'surface/accent/cyan': semantic('color/cyan/100', 'overlay/black/20'),
    'surface/indicator/bold': semantic('overlay/black/60', 'color/white'),
    'surface/indicator/subtle': semantic('color/white', 'overlay/black/60'),
    'surface/indicator/primary': semantic('color/blue/700', 'color/white'),
    'surface/progress/track': semantic('overlay/black/10', 'overlay/white/10'),
    'surface/progress/fill': semantic('color/cyan/600', 'color/white'),
    'surface/progress/fill-overall': semantic('color/cyan/600', 'color/cyan/300'),
    'surface/progress/segment': semantic('overlay/white/80', 'overlay/black/40'),
    'surface/carousel-indicator/active': semantic('overlay/black/60', 'overlay/white/60'),
    'surface/carousel-indicator/inactive': semantic('overlay/black/20', 'overlay/white/20'),
    'surface/graph/screening/minimal': semantic(lit('#75B8E7'), lit('#75B8E7')),
    'surface/graph/screening/mild': semantic(lit('#77F094'), lit('#77F094')),
    'surface/graph/screening/moderate': semantic(lit('#F7FA81'), lit('#F7FA81')),
    'surface/graph/screening/moderately-severe': semantic(lit('#FCC067'), lit('#FCC067')),
    'surface/graph/screening/severe': semantic(lit('#F07889'), lit('#F07889')),
    'surface/graph/screening/marker': semantic('overlay/black/20', 'overlay/white/20'),
    'surface/feedback/error-bold': semantic('color/red/800', 'color/red/200'),
    'surface/feedback/error-subtle': semantic('color/red/200', 'color/red/900'),
    'surface/feedback/warning-bold': semantic('color/orange/800', 'color/orange/200'),
    'surface/feedback/warning-subtle': semantic('color/orange/200', 'color/orange/900'),
    'surface/feedback/success-bold': semantic('color/green/700', 'color/green/100'),
    'surface/feedback/success-subtle': semantic('color/green/100', 'color/green/900'),
    'surface/feedback/info-bold': semantic('color/cyan/700', 'color/cyan/100'),
    'surface/feedback/info-subtle': semantic('color/cyan/100', 'color/cyan/900'),
    'surface/picker/switch-track-on': semantic('color/cyan/600', 'color/cyan/300'),
    'surface/picker/switch-track-off': semantic('color/neutral/200', 'overlay/black/40'),
    'surface/picker/switch-track-disabled': semantic('overlay/black/10', 'overlay/white/20'),
    'surface/picker/switch-thumb': semantic('color/white', 'color/white'),
    'surface/picker/switch-thumb-disabled': semantic('overlay/white/40', 'overlay/white/40'),
    'surface/nav-bar': semantic('color/white', 'color/purple/950'),
    'surface/nav-android/bg': semantic('overlay/white/80', 'overlay/black/60'),
    'surface/nav-android/select': semantic('overlay/black/5', 'overlay/white/10'),
    'surface/emotion/tag': semantic('overlay/white/20', 'overlay/black/20'),
    'surface/emotion/tag-badge': semantic('color/neutral/100', 'overlay/white/10'),
    'surface/emotion/button': semantic('color/white', 'overlay/black/40'),
    'surface/emotion/graph-marker': semantic('overlay/black/20', 'overlay/white/40'),
    'surface/emotion/icon/anger': semantic(lit('#FFF6F6'), lit('#454164')),
    'surface/emotion/icon/disgust': semantic(lit('#EEF8ED'), lit('#2C5170')),
    'surface/emotion/icon/enjoyment': semantic(lit('#FEF9EC'), lit('#47506A')),
    'surface/emotion/icon/fear': semantic(lit('#F2F2FE'), lit('#354B84')),
    'surface/emotion/icon/sadness': semantic(lit('#E5F3FD'), lit('#274773')),
    // Identical light/dark values are intentional: these tokens do not flip with theme.
    'surface/indicator/hero-card/default-content': semantic('overlay/black/40', 'overlay/black/40'),
    'surface/indicator/hero-card/default-non-content': semantic('overlay/white/80', 'overlay/white/80'),
    'surface/progress/hero-card/track': semantic('overlay/white/40', 'overlay/white/40'),
    'surface/progress/hero-card/fill': semantic('color/white', 'color/white'),
    'surface/hero-card/scrim-content': semantic('overlay/black/40', 'overlay/black/40'),
    'surface/hero-card/scrim-non-content': semantic('overlay/white/80', 'overlay/white/80'),
    // --- Text (55) ---
    'text/heading': semantic('color/blue/900', 'color/blue/100'),
    'text/default': semantic('color/neutral/800', 'color/white'),
    'text/sub': semantic('color/neutral/600', 'color/neutral/200'),
    'text/accent': semantic('color/blue/700', 'color/neutral/200'),
    'text/disabled': semantic('color/neutral/500', 'color/neutral/200'),
    'text/interactive/primary': semantic('color/blue/700', 'color/white'),
    'text/interactive/primary-pressed': semantic('color/blue/900', 'color/neutral/200'),
    'text/interactive/primary-hover': semantic('color/blue/800', 'color/neutral/100'),
    'text/interactive/secondary': semantic('color/purple/700', 'color/white'),
    'text/interactive/secondary-pressed': semantic('color/purple/900', 'color/neutral/200'),
    'text/interactive/secondary-hover': semantic('color/purple/800', 'color/neutral/100'),
    'text/on-interactive/default': semantic('color/white', 'color/blue/800'),
    'text/on-interactive/secondary': semantic('color/purple/700', 'color/white'),
    'text/on-interactive/dialog-bold': semantic('color/blue/800', 'color/white'),
    'text/on-interactive/disabled': semantic('color/white', 'color/neutral/200'),
    'text/on-dialog-bold': semantic('color/white', 'color/neutral/800'),
    'text/on-indicator/bold': semantic('color/white', 'color/neutral/800'),
    'text/on-indicator/subtle': semantic('color/neutral/800', 'color/white'),
    'text/on-feedback/error-bold': semantic('color/red/200', 'color/red/800'),
    'text/on-feedback/error-subtle': semantic('color/red/800', 'color/red/200'),
    'text/on-feedback/warning-bold': semantic('color/orange/200', 'color/orange/800'),
    'text/on-feedback/warning-subtle': semantic('color/orange/800', 'color/orange/200'),
    'text/on-feedback/success-bold': semantic('color/green/100', 'color/green/800'),
    'text/on-feedback/success-subtle': semantic('color/green/700', 'color/green/100'),
    'text/on-feedback/info-bold': semantic('color/cyan/100', 'color/cyan/800'),
    'text/on-feedback/info-subtle': semantic('color/cyan/700', 'color/cyan/100'),
    'text/feedback/error': semantic('color/red/800', 'color/red/400'),
    'text/feedback/warning': semantic('color/orange/800', 'color/orange/200'),
    'text/feedback/success': semantic('color/green/700', 'color/green/100'),
    'text/feedback/info': semantic('color/cyan/700', 'color/cyan/100'),
    'text/on-callout/default': semantic('color/white', 'color/neutral/800'),
    'text/on-callout/subtle': semantic('color/neutral/50', 'color/neutral/600'),
    'text/on-surface/tile': semantic('color/neutral/800', 'color/white'),
    'text/on-surface/tile-selected': semantic('color/white', 'color/neutral/800'),
    'text/on-surface/tile-disabled': semantic('color/neutral/500', 'color/neutral/300'),
    'text/on-surface/tile-subtle': semantic('color/neutral/600', 'color/white'),
    // Identical light/dark values are intentional: the emotion bubble artwork is
    // raster and has no dark variant, so its label stays on the light treatment.
    'text/on-emotion/default': semantic('color/neutral/800', 'color/neutral/800'),
    'text/emotion/anger': semantic(lit('#D52020'), lit('#FF9A9A')),
    'text/emotion/disgust': semantic(lit('#0B7143'), lit('#88DEB6')),
    'text/emotion/enjoyment': semantic(lit('#9A5680'), lit('#FFEE84')),
    'text/emotion/fear': semantic(lit('#4B4BC4'), lit('#BBB6FF')),
    'text/emotion/sadness': semantic(lit('#264BB5'), lit('#63C2FF')),
    // `-content` = dark treatment, `-non-content` = light treatment. Identical
    // light/dark values are intentional: these tokens do not flip with theme.
    'text/hero-card/title-non-content': semantic('color/blue/900', 'color/blue/900'),
    'text/hero-card/default-content': semantic('color/white', 'color/white'),
    'text/hero-card/default-non-content': semantic('color/neutral/800', 'color/neutral/800'),
    'text/hero-card/sub-content': semantic('color/neutral/200', 'color/neutral/200'),
    'text/hero-card/sub-non-content': semantic('color/neutral/600', 'color/neutral/600'),
    'text/hero-card/category-content': semantic('color/white', 'color/white'),
    'text/hero-card/category-non-content': semantic('color/cyan/700', 'color/cyan/700'),
    'text/hero-card/link-content': semantic('color/blue/700', 'color/blue/700'),
    'text/hero-card/link-non-content': semantic('color/blue/700', 'color/blue/700'),
    'text/on-indicator/hero-card/default-content': semantic('color/white', 'color/white'),
    'text/on-indicator/hero-card/default-non-content': semantic('color/blue/700', 'color/blue/700'),
    'text/on-interactive/hero-card/primary-content': semantic('color/blue/700', 'color/blue/700'),
    'text/on-interactive/hero-card/primary-non-content': semantic('color/white', 'color/white'),
    // --- Icon (35) ---
    'icon/default': semantic('color/neutral/600', 'color/white'),
    'icon/primary': semantic('color/blue/700', 'color/white'),
    'icon/secondary': semantic('color/purple/700', 'color/white'),
    'icon/disabled': semantic('color/neutral/200', 'color/neutral/500'),
    'icon/on-interactive/default': semantic('color/white', 'color/blue/800'),
    'icon/on-interactive/disabled': semantic('color/white', 'color/neutral/200'),
    'icon/on-interactive/secondary': semantic('color/white', 'color/purple/700'),
    'icon/on-dialog-bold': semantic('color/white', 'color/neutral/800'),
    'icon/on-accent/purple': semantic('color/purple/700', 'color/white'),
    'icon/on-accent/cyan': semantic('color/blue/500', 'color/cyan/400'),
    'icon/feedback/error': semantic('color/red/700', 'color/red/400'),
    'icon/feedback/warning': semantic('color/orange/700', 'color/orange/400'),
    'icon/feedback/success': semantic('color/green/600', 'color/green/300'),
    'icon/feedback/info': semantic('color/cyan/600', 'color/cyan/300'),
    'icon/on-callout/default': semantic('color/white', 'color/neutral/800'),
    'icon/on-indicator/default': semantic('color/white', 'color/blue/800'),
    'icon/on-surface/tile': semantic('color/purple/700', 'color/white'),
    'icon/on-surface/tile-selected': semantic('color/white', 'color/purple/700'),
    'icon/on-surface/tile-disabled': semantic('color/neutral/500', 'color/neutral/300'),
    'icon/on-surface/tile-check': semantic('color/white', 'color/neutral/600'),
    'icon/on-feedback/bold': semantic('color/white', 'color/neutral/600'),
    'icon/emotion/anger': semantic(lit('#D52020'), lit('#FF9A9A')),
    'icon/emotion/disgust': semantic(lit('#0B7143'), lit('#88DEB6')),
    'icon/emotion/enjoyment': semantic(lit('#9A5680'), lit('#FFEE84')),
    'icon/emotion/fear': semantic(lit('#4B4BC4'), lit('#BBB6FF')),
    'icon/emotion/sadness': semantic(lit('#264BB5'), lit('#63C2FF')),
    // Calm Health Badge — standard (non-hero) card default. Mode-AWARE: flips
    // with system theme. Used for ~99% of badge instances; the hero-card variants
    // below are the mode-locked exceptions.
    'icon/badge/calm-health/icon': semantic('color/cyan/600', 'color/cyan/300'),
    'icon/badge/calm-health/letter': semantic('color/white', 'color/cyan/900'),
    'icon/brand/calm-health/logo': semantic('color/blue/700', 'color/white'),
    // Calm Health Badge — hero-card variants. Mode-locked (identical light/dark)
    // to the card's fixed treatment, nested under the component per the
    // 2026-06-23 convention that retired the `-on-dark`/`-on-light` suffix.
    // `-content` = dark-treatment card, `-non-content` = light-treatment card.
    'icon/badge/calm-health/hero-card/icon-content': semantic('color/cyan/300', 'color/cyan/300'),
    'icon/badge/calm-health/hero-card/icon-non-content': semantic('color/cyan/600', 'color/cyan/600'),
    'icon/badge/calm-health/hero-card/letter-content': semantic('color/cyan/900', 'color/cyan/900'),
    'icon/badge/calm-health/hero-card/letter-non-content': semantic('color/white', 'color/white'),
    // Identical light/dark values are intentional: these tokens do not flip with theme.
    'icon/on-indicator/hero-card/primary-content': semantic('color/white', 'color/white'),
    'icon/on-indicator/hero-card/primary-non-content': semantic('color/blue/700', 'color/blue/700'),
    // --- Glass (12) ---
    'glass/bright/selected': semantic('color/blue/700', 'color/white'),
    'glass/bright/unselected': semantic('color/neutral/600', 'color/neutral/600'),
    'glass/dim/selected': semantic('color/white', 'color/white'),
    'glass/dim/unselected': semantic('color/neutral/100', 'color/neutral/100'),
    'glass/progress-fab/bright-bg': semantic('color/purple/700', 'color/purple/700'),
    'glass/progress-fab/bright-track': semantic('overlay/white/20', 'overlay/white/20'),
    'glass/progress-fab/bright-progress': semantic('color/purple/100', 'color/purple/100'),
    'glass/progress-fab/bright-content': semantic('color/white', 'color/white'),
    'glass/progress-fab/dim-bg': semantic('color/purple/100', 'color/purple/100'),
    'glass/progress-fab/dim-track': semantic('overlay/black/20', 'overlay/black/20'),
    'glass/progress-fab/dim-progress': semantic('color/purple/700', 'color/purple/700'),
    'glass/progress-fab/dim-content': semantic('color/neutral/800', 'color/neutral/800'),
    // --- Interactive (18) ---
    'interactive/primary': semantic('color/blue/700', 'color/white'),
    'interactive/primary-pressed': semantic('color/blue/900', 'color/neutral/200'),
    'interactive/primary-hover': semantic('color/blue/800', 'color/neutral/100'),
    'interactive/primary-outline-pressed': semantic('overlay/white/40', 'overlay/black/40'),
    'interactive/primary-outline-hover': semantic('overlay/white/20', 'overlay/black/20'),
    'interactive/secondary': semantic('color/purple/700', 'color/purple/100'),
    'interactive/secondary-pressed': semantic('color/purple/900', 'color/purple/300'),
    'interactive/secondary-hover': semantic('color/purple/800', 'color/purple/200'),
    'interactive/disabled': semantic('overlay/black/20', 'overlay/white/20'),
    'interactive/disabled-solid': semantic('color/neutral/300', 'color/neutral/300'),
    'interactive/dialog-bold': semantic('color/white', 'color/blue/700'),
    'interactive/dialog-bold-hover': semantic('color/neutral/100', 'color/blue/800'),
    'interactive/dialog-bold-pressed': semantic('color/neutral/200', 'color/blue/900'),
    // Identical light/dark values are intentional: these tokens do not flip with theme.
    'interactive/hero-card/primary-content': semantic('color/white', 'color/white'),
    'interactive/hero-card/primary-non-content': semantic('color/blue/700', 'color/blue/700'),
    // Subtle wash for the resting/hover/pressed states of controls that sit on a
    // gradient background (unselected filter chips, segmented tracks). Hover and
    // pressed always darken in BOTH modes — a gradient has no fixed base to lift
    // against, so emphasis stays direction-consistent (matching how filled
    // buttons deepen on press). Flat static surfaces use surface/hover/-pressed.
    'interactive/subtle': semantic('color/white', 'overlay/black/20'),
    'interactive/subtle-hover': semantic('color/neutral/100', 'overlay/black/40'),
    'interactive/subtle-pressed': semantic('color/neutral/200', 'overlay/black/60'),
    // --- Border (25; +1 code-only focus below = 26) ---
    'border/interactive': semantic('color/blue/700', 'color/white'),
    'border/interactive-hover': semantic('color/blue/800', 'color/neutral/100'),
    'border/interactive-pressed': semantic('color/blue/900', 'color/neutral/200'),
    'border/interactive-secondary': semantic('color/purple/700', 'color/purple/100'),
    'border/interactive-secondary-hover': semantic('color/purple/800', 'color/purple/200'),
    'border/interactive-secondary-pressed': semantic('color/purple/900', 'color/purple/300'),
    'border/divider': semantic('overlay/black/10', 'overlay/white/20'),
    'border/disabled': semantic('overlay/black/40', 'overlay/white/40'),
    'border/error': semantic('color/red/700', 'color/red/400'),
    'border/warning': semantic('color/orange/700', 'color/orange/400'),
    'border/info': semantic('color/cyan/600', 'color/cyan/300'),
    'border/success': semantic('color/green/600', 'color/green/200'),
    'border/picker': semantic('overlay/black/20', 'overlay/white/10'),
    'border/interactive-tile': semantic('overlay/black/10', 'overlay/white/20'),
    'border/interactive-card': semantic('overlay/black/10', 'overlay/white/10'),
    'border/input': semantic('color/neutral/600', 'color/neutral/300'),
    'border/subtle': semantic('overlay/black/10', 'overlay/white/20'),
    // Identical light/dark values are intentional: this stroke sits on the
    // mode-invariant screening-graph surface and does not flip with theme.
    'border/graph-screening-scale': semantic('overlay/white/60', 'overlay/white/60'),
    'border/emotion/anger': semantic(lit('#D52020'), lit('#FF9A9A')),
    'border/emotion/disgust': semantic(lit('#0B7143'), lit('#88DEB6')),
    'border/emotion/enjoyment': semantic(lit('#9A5680'), lit('#FFEE84')),
    'border/emotion/fear': semantic(lit('#4B4BC4'), lit('#BBB6FF')),
    'border/emotion/sadness': semantic(lit('#264BB5'), lit('#63C2FF')),
    // Identical light/dark values are intentional: these tokens do not flip with theme.
    'border/hero-card/primary-content': semantic('color/white', 'color/white'),
    'border/hero-card/primary-non-content': semantic('color/blue/700', 'color/blue/700'),
    // --- Gradient (40): each gradient is a start + end stop the component composites ---
    'gradient/background/primary start': semantic('color/blue/100', 'color/purple/900'),
    'gradient/background/primary end': semantic('color/orange/100', 'color/blue/700'),
    'gradient/background/secondary start': semantic('color/purple/100', 'color/purple/800'),
    'gradient/background/secondary end': semantic('color/red/100', 'color/purple/600'),
    'gradient/foreground/primary start': semantic('color/purple/700', 'color/purple/700'),
    'gradient/foreground/primary end': semantic('color/blue/500', 'color/blue/500'),
    'gradient/foreground/secondary start': semantic('color/purple/700', 'color/purple/700'),
    'gradient/foreground/secondary end': semantic('color/purple/500', 'color/purple/500'),
    'gradient/emotion/background/enjoyment start': semantic(lit('#FBF7D9'), lit('#726544')),
    'gradient/emotion/background/enjoyment end': semantic(lit('#FFF4F0'), lit('#73485C')),
    'gradient/emotion/background/anger start': semantic(lit('#FFECEC'), lit('#662B36')),
    'gradient/emotion/background/anger end': semantic(lit('#FFF7F7'), lit('#753A45')),
    'gradient/emotion/background/disgust start': semantic(lit('#DDF3E8'), lit('#224240')),
    'gradient/emotion/background/disgust end': semantic(lit('#F3FDF8'), lit('#235F6E')),
    'gradient/emotion/background/sadness start': semantic(lit('#D7E5FB'), lit('#203764')),
    'gradient/emotion/background/sadness end': semantic(lit('#E6F4FE'), lit('#214C70')),
    'gradient/emotion/background/fear start': semantic(lit('#E1E1F8'), lit('#2F3767')),
    'gradient/emotion/background/fear end': semantic(lit('#F4F3FF'), lit('#452B6D')),
    'gradient/emotion/graph/enjoyment start': semantic(lit('#FFE974'), lit('#FFE974')),
    'gradient/emotion/graph/enjoyment end': semantic(lit('#F78CB1'), lit('#F78CB1')),
    'gradient/emotion/graph/anger start': semantic(lit('#F79999'), lit('#F79999')),
    'gradient/emotion/graph/anger end': semantic(lit('#F82424'), lit('#F82424')),
    'gradient/emotion/graph/disgust start': semantic(lit('#84E1C2'), lit('#84E1C2')),
    'gradient/emotion/graph/disgust end': semantic(lit('#039453'), lit('#039453')),
    'gradient/emotion/graph/sadness start': semantic(lit('#3EAFF6'), lit('#3EAFF6')),
    'gradient/emotion/graph/sadness end': semantic(lit('#264BB5'), lit('#264BB5')),
    'gradient/emotion/graph/fear start': semantic(lit('#A198F2'), lit('#A198F2')),
    'gradient/emotion/graph/fear end': semantic(lit('#4849BF'), lit('#4849BF')),
    'gradient/dialog/bold start': semantic('color/purple/700', 'color/blue/100'),
    'gradient/dialog/bold end': semantic('color/blue/600', 'color/orange/100'),
    'gradient/media-scrim start': semantic('overlay/black/0', 'overlay/black/0'),
    'gradient/media-scrim mid': semantic('overlay/black/60', 'overlay/black/60'),
    'gradient/media-scrim end': semantic('overlay/black/80', 'overlay/black/80'),
    'gradient/fade start': semantic('overlay/white/0', 'overlay/black/0'),
    'gradient/fade mid': semantic('color/white', 'overlay/black/20'),
    'gradient/fade end': semantic('overlay/white/0', 'overlay/black/0'),
    'gradient/callout/bold start': semantic('color/purple/700', 'color/cyan/100'),
    'gradient/callout/bold end': semantic('color/blue/500', 'color/cyan/300'),
    'gradient/interactive/tile-selected start': semantic('color/purple/600', 'color/purple/100'),
    'gradient/interactive/tile-selected end': semantic('color/purple/800', 'color/purple/200'),
    // --- Effects (7): shadow/blur primitives, dimension $type ---
    'effects/blur/low': semantic('blur/sm', 'blur/sm', 'dimension'),
    'effects/blur/medium': semantic('blur/md', 'blur/md', 'dimension'),
    'effects/blur/high': semantic('blur/lg', 'blur/lg', 'dimension'),
    'effects/blur/intense': semantic('blur/xl', 'blur/xl', 'dimension'),
    // Dark mode steps each shadow down one level (per design).
    'effects/shadows/low': semantic('shadow/sm', 'shadow/xs', 'dimension'),
    'effects/shadows/medium': semantic('shadow/md', 'shadow/sm', 'dimension'),
    'effects/shadows/high': semantic('shadow/lg', 'shadow/md', 'dimension'),
    // --- Spacing (13): dimension semantics aliasing the same-named primitive ---
    // Value-named and mode-locked; the design intent lives in the Figma
    // description each token carries, not in the name.
    'spacing/2': semantic('spacing/2', 'spacing/2', 'dimension'),
    'spacing/4': semantic('spacing/4', 'spacing/4', 'dimension'),
    'spacing/8': semantic('spacing/8', 'spacing/8', 'dimension'),
    'spacing/12': semantic('spacing/12', 'spacing/12', 'dimension'),
    'spacing/16': semantic('spacing/16', 'spacing/16', 'dimension'),
    'spacing/20': semantic('spacing/20', 'spacing/20', 'dimension'),
    'spacing/24': semantic('spacing/24', 'spacing/24', 'dimension'),
    'spacing/32': semantic('spacing/32', 'spacing/32', 'dimension'),
    'spacing/40': semantic('spacing/40', 'spacing/40', 'dimension'),
    'spacing/layout/48': semantic('spacing/48', 'spacing/48', 'dimension'),
    'spacing/layout/64': semantic('spacing/64', 'spacing/64', 'dimension'),
    'spacing/layout/96': semantic('spacing/96', 'spacing/96', 'dimension'),
    'spacing/layout/128': semantic('spacing/128', 'spacing/128', 'dimension'),
    // --- Corner radius (11): Figma-faithful path names (the repo's primitive
    // group is `radius`; the exporter's ref index aliases across the two). ---
    'corner radius/0': semantic('corner radius/0', 'corner radius/0', 'dimension'),
    'corner radius/2': semantic('corner radius/2', 'corner radius/2', 'dimension'),
    'corner radius/4': semantic('corner radius/4', 'corner radius/4', 'dimension'),
    'corner radius/8': semantic('corner radius/8', 'corner radius/8', 'dimension'),
    'corner radius/12': semantic('corner radius/12', 'corner radius/12', 'dimension'),
    'corner radius/16': semantic('corner radius/16', 'corner radius/16', 'dimension'),
    'corner radius/20': semantic('corner radius/20', 'corner radius/20', 'dimension'),
    'corner radius/24': semantic('corner radius/24', 'corner radius/24', 'dimension'),
    'corner radius/32': semantic('corner radius/32', 'corner radius/32', 'dimension'),
    'corner radius/40': semantic('corner radius/40', 'corner radius/40', 'dimension'),
    'corner radius/full': semantic('corner radius/full', 'corner radius/full', 'dimension'),
    // Keyboard focus ring.
    // EVE-12884: the two hexes were transposed between modes (each only clears
    // 3:1 non-text contrast on the OTHER mode's surfaces). Swapped to match colors.ts.
    'border/interactive-focus': semantic(lit('#303030'), lit('#84FFFF')),
};
/**
 * Split each slash-path into a nested tree, PascalCasing every segment to match
 * the DTCG token tree the cross-platform exporter emits (`Surface.Dialog.Bold`,
 * mirroring `TOKENS_PRIMITIVES`). The canonical, lossless token identity stays
 * the slash-path key in `SEMANTICS_SRC` (what `resolve()` reads); this tree is
 * the exporter-facing projection of it — the one nested view we expose.
 */
function nest(src) {
    const root = {};
    for (const [path, token] of Object.entries(src)) {
        // Attach Figma's design-intent description (keyed by the slash-path) as DTCG
        // `$description`. It carries no `$value`, so the emitted DS_*/Kotlin/Swift are
        // unaffected; it just travels with the token for downstream consumers.
        const $description = FIGMA_DESCRIPTIONS[path];
        const leaf = $description ? { ...token, $description } : token;
        const segs = path.split('/');
        let node = root;
        segs.forEach((seg, i) => {
            const key = toPascal(seg);
            if (i === segs.length - 1) {
                node[key] = leaf;
            }
            else {
                node[key] ??= {};
                node = node[key];
            }
        });
    }
    return root;
}
function toPascal(seg) {
    return seg
        .split(/[-\s]/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join('');
}
/** PascalCase DTCG tree the cross-platform exporter reads (emit is a later step). */
export const TOKENS_SEMANTICS = nest(SEMANTICS_SRC);
export { resolve } from './resolve';
export { LEGACY_SEMANTICS_SRC, TOKENS_LEGACY_SEMANTICS, resolveLegacy, nest, toPascal, } from './legacy';
//# sourceMappingURL=index.js.map